import { useState, useEffect, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { billRefLabel } from '../../../../shared/contracts/bill-ref.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { personLabel } from '../../components/ApprovalLevels.js';

/** L15.08 — Payment Holds: manage held payments (release / extend / escalate). */
export function HoldsPage(): ReactNode {
  const { t } = useTranslation();
  // Amounts follow the tenant's DISPLAY_NUMBER_FORMAT, not a locale in the source.
  const { money } = useBusinessNumber();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { atDay, day } = useBusinessDay();
  const { token } = useAuth();
  const [holds, setHolds] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  const [releaseReason, setReleaseReason] = useState('');

  useEffect(() => {
    apiRequest<any[]>('/v1/payments/hold/active', { token })
      .then(setHolds)
      .catch(e => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleRelease = async (holdId: string) => {
    if (!releaseReason.trim()) {
      setError(t('finance.holds.reasonRequired'));
      return;
    }
    try {
      await apiRequest<any>(`/v1/payments/hold/${holdId}/release`, { token, method: 'PATCH', body: { releaseReason: releaseReason.trim() } });
      setHolds(prev => prev.filter(h => h.holdId !== holdId));
      setReleasingId(null);
      setReleaseReason('');
      setError(null);
    } catch (e) { setError((e as Error).message); }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>{t('finance.holds.title')}</h1>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {loading && <p>{t('finance.holds.loading')}</p>}
      {!loading && holds.length === 0 && <p>{t('finance.holds.empty')}</p>}
      {holds.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg-muted)', borderBottom: '1px solid var(--color-border-default)' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.holds.colClaim')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.holds.colVendor')}</th>
              <th style={{ padding: 8, textAlign: 'right' }}>{t('finance.holds.colAmount')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.holds.colReason')}</th>
              <th style={{ padding: 8, textAlign: 'left' }}>{t('finance.holds.colHoldDate')}</th>
              <th style={{ padding: 8 }}>{t('finance.holds.colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {holds.map((h, i) => (
              <tr key={h.holdId ?? i} style={{ borderBottom: '1px solid var(--color-border-default)' }}>
                <td style={{ padding: 8 }}>{billRefLabel(h.bill, day)}</td>
                <td style={{ padding: 8 }}>{personLabel(h.vendorName, h.vendorCode) ?? '—'}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{money(h.holdAmount ?? 0)}</td>
                <td style={{ padding: 8 }}>{h.holdReasonCode}</td>
                <td style={{ padding: 8 }}>{h.holdDate ? atDay(h.holdDate) : '—'}</td>
                <td style={{ padding: 8, textAlign: 'center' }}>
                  {releasingId === h.holdId ? (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', justifyContent: 'center' }}>
                      <input data-testid={`release-reason-${h.holdId}`} value={releaseReason} onChange={e => setReleaseReason(e.target.value)} placeholder={t('finance.holds.reasonPlaceholder')} style={{ padding: 4, borderRadius: 4, border: '1px solid var(--color-border-default)' }} />
                      <button onClick={() => handleRelease(h.holdId)} style={{ padding: '4px 12px', background: 'var(--color-success)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('finance.holds.confirm')}</button>
                      <button onClick={() => { setReleasingId(null); setReleaseReason(''); setError(null); }} style={{ padding: '4px 8px', background: 'var(--color-text-muted)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('finance.holds.cancel')}</button>
                    </div>
                  ) : (
                    <button onClick={() => { setReleasingId(h.holdId); setReleaseReason(''); }} style={{ padding: '4px 12px', background: 'var(--color-success)', color: 'var(--color-text-inverse)', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('finance.holds.release')}</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
